/**
 * StaticPage — thin layout atom for full-screen static overlay pages.
 *
 * Composes IOSScreen + PageHeader + padded scrollable content area.
 * Back behaviour is injected via `onBack` prop (props-in pattern — atoms cannot
 * use hooks). The owning screen calls usePlatformBack() to wire the TG back
 * button, then passes the same callback (or navigate(-1)) here so the visual
 * back pill in PageHeader is consistent.
 *
 * Atom purity: imports only react + tokens + other atoms (IOSScreen, PageHeader).
 * Enforced by atom-purity.arch.test.ts.
 */
import { CSSProperties, ReactNode } from 'react';
import { IOSScreen } from './IOSScreen';
import { PageHeader } from './PageHeader';

export interface StaticPageProps {
  /** Screen title rendered as PageHeader large-title. */
  title: string;
  /** Optional subtitle rendered below the large title (e.g. "Частые вопросы"). */
  subtitle?: string;
  /** Content of the page, rendered in a padded scroll region. */
  children: ReactNode;
  /**
   * Optional back handler forwarded to PageHeader's back pill.
   * Screens typically pass the same function registered via usePlatformBack().
   * When omitted the back pill is hidden (PageHeader.onBack is optional).
   */
  onBack?: () => void;
}

const contentStyle: CSSProperties = {
  padding: '14px 16px 32px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

export function StaticPage({ title, subtitle, children, onBack }: StaticPageProps) {
  return (
    <IOSScreen>
      <PageHeader title={title} subtitle={subtitle} onBack={onBack} />
      <div style={contentStyle}>{children}</div>
    </IOSScreen>
  );
}
